import moment from 'moment';
import React from 'react';
import {
  EmitterSubscription,
  Keyboard,
  Platform,
  ScrollView,
  StyleSheet,
  TouchableWithoutFeedback,
  View,
} from 'react-native';
import {withForwardedNavigationParams} from 'react-navigation-props-mapper';
import ScreenContext from '../../ScreenContext';
import NavActions from 'src/actions/NavActions';
import Events from 'src/logging/Events';
import AVTextInput from '../../elements/AVTextInput';
import withScrollToElement from '../../hoc/withScrollToElement';
import type {ScrollToElementProps} from 'src/types/Screen';
import ActionsFactory from 'src/actions/ActionsFactory';
import Styles from '../../Styles';
import type {Referral} from 'src/types/Referral';
import {HttpResponseCodes} from 'src/api/Api';
import Localized from 'src/constants/AppStrings';
import {alertError, alertInProgress} from '../../helpers/AlertHelper';
import AVTouchableOpacity from 'src/components/elements/AVTouchableOpacity';
import AVText from 'src/components/elements/AVText';
import {ScreenWidth} from 'react-native-elements/dist/helpers';
import BackSubheader from 'src/components/elements/BackSubheader';
import EmailLink from 'src/components/img/svg/EmailLink';
import {isValidEmail} from 'src/services/ValidatorService';
import Settings from 'src/Settings';
import PasswordInput from '../../PasswordInput';
import {Auth0Signup} from 'src/types/SetupModel';

type CreateAccountEnterEmailProps = ScrollToElementProps & {
  location: string;
  privacyVersion: string;
  startTime?: moment.Moment;
  referral: Referral | null | undefined;
  isPasswordValid: boolean;
};
type CreateAccountEnterEmailState = {
  email: string;
  password: string;
  isKeyboardOpen: boolean;
  errorMessage: string[];
};
const MIN_PASSWORD_LENGTH = 8;

class CreateAccountEnterEmail extends React.Component<
  CreateAccountEnterEmailProps,
  CreateAccountEnterEmailState
> {
  static defaultProps = {
    location: null,
  };

  static contextType = ScreenContext;
  subscriptions: Array<any>;
  email: AVTextInput | null;
  password: AVTextInput | null;
  scrollView: ScrollView | null;
  keyboardDidShowListener: EmitterSubscription;
  keyboardDidHideListener: EmitterSubscription;
  constructor(props: CreateAccountEnterEmailProps) {
    super(props);
    this.state = {
      email: '',
      password: '',
      isKeyboardOpen: false,
      errorMessage: [],
    };
    this.handleBack = this.handleBack.bind(this);
    this.displayAccountSetupError = this.displayAccountSetupError.bind(this);
    this.handleClick = this.handleClick.bind(this);
    this.handleCancel = this.handleCancel.bind(this);
    this.keyboardDidShow = this.keyboardDidShow.bind(this);
    this.keyboardDidHide = this.keyboardDidHide.bind(this);
    this.password = null;
  }

  componentDidMount() {
    this.keyboardDidShowListener = Keyboard.addListener(
      'keyboardDidShow',
      this.keyboardDidShow,
    );
    this.keyboardDidHideListener = Keyboard.addListener(
      'keyboardDidHide',
      this.keyboardDidHide,
    );
  }

  componentWillUnmount() {
    this.keyboardDidShowListener.remove();
    this.keyboardDidHideListener.remove();
  }

  keyboardDidShow() {
    this.setState({isKeyboardOpen: true});
  }

  keyboardDidHide() {
    this.setState({isKeyboardOpen: false});
  }

  handleCancel() {
    const startTime = this.props.startTime ?? moment();
    Events.AccountCreation.trackEvent(startTime, 'EnterEmail');
  }

  handleBack() {
    NavActions.popToTop();
  }

  displayAccountSetupError(error: any) {
    let message = Localized.Errors.problem_creating_account;

    if (error.statusCode === HttpResponseCodes.Conflict) {
      message += ` ${Localized.Errors.email_already_exists}`;

      if (!message.endsWith('.')) {
        message += '.';
      }
    } else {
      message += ` ${Localized.Errors.check_internet_connection}`;
    }

    alertError(message);
  }

  async handleClick() {
    const {email, password, errorMessage} = this.state;

    if (!email || !password) {
      alertError(Localized.Errors.all_fields_required);
      return;
    }
    if (errorMessage.length > 0) {
      alertError(errorMessage.join('\n'));
    } else {
      this.context.actions.showSpinner();

      try {
        alertInProgress(
          Localized.Success.create_account_instructions,
          async () => {
            try {

              const signupModel: Auth0Signup = {
                email: email,
                password: password,
              };

              const response = await ActionsFactory.getAccountActions().setupAccountAuth0(
                signupModel,
              );
              Events.AccountCreation.trackEvent(
                this.props.startTime,
                '',
                `Create new account ${JSON.stringify(response)}`,
              );
              this.handleBack();
            } catch (error) {
              this.displayAccountSetupError(error);
            }
          },
        );
      } catch (error) {
        this.displayAccountSetupError(error);
      } finally {
        this.context.actions.hideSpinner();
      }
    }
  }

  render() {
    if (Platform.OS === 'web') {
      return this.renderWeb();
    }

    return (
      <BackSubheader
        title={Localized.Labels.get_you_going}
        onBackSelect={this.handleCancel}
      >
        <TouchableWithoutFeedback onPress={Keyboard.dismiss}>
          <View style={styles.container}>
            <AVText style={styles.instructions}>
              {Localized.Labels.enter_email_for_confirmation}
            </AVText>
            <View style={[Styles.Style.maxWidthContainer, styles.bottomRow]}>
              <AVTextInput
                label={Localized.Labels.email}
                accessibilityLabel="Email"
                autoCapitalize="none"
                onSubmitEditing={() => this.password?.focus()}
                returnKeyType="next"
                onChangeText={(text) =>
                  this.setState({
                    email: text,
                  })
                }
                keyboardType={'email-address'}
                value={this.state.email}
                onFocus={() => this.setState({isKeyboardOpen: true})}
              />

              <PasswordInput
                needValidation
                accessibilityLabel="Password"
                label={Localized.Labels.password}
                forwardRef={(r) => (this.password = r)}
                value={this.state.password}
                onFocus={() => this.setState({isKeyboardOpen: true})}
                onSubmitEditing={() => this.handleClick()}
                onChangeText={(password, errorMessage) =>
                  this.setState({
                    password,
                    errorMessage,
                  })
                }
              />
            </View>
            {!this.state.isKeyboardOpen && (
              <AVTouchableOpacity style={styles.box} onPress={this.handleClick}>
                <View style={styles.letter}>
                  <EmailLink size={ScreenWidth * 0.2} />
                </View>
                <AVText style={styles.descText}>
                  {Localized.Labels.send_link_to_email}
                </AVText>
                <AVText style={styles.clickText}>
                  {Localized.Labels.click_here_to_continue}
                </AVText>
              </AVTouchableOpacity>
            )}
          </View>
        </TouchableWithoutFeedback>
      </BackSubheader>
    );
  }

  renderWeb() {
    return (
      <BackSubheader title={Localized.Labels.get_you_going}>
        <View style={[Styles.Style.flex, styles.container]}>
          <View style={[Styles.Style.flex, styles.bottomRow]}>
            <AVText style={styles.title}>
              {Localized.Labels.enter_email_for_confirmation}
            </AVText>
            <AVTextInput
              label={Localized.Labels.email}
              accessibilityLabel="Email"
              autoCapitalize="none"
              onSubmitEditing={() => this.password?.focus()}
              returnKeyType="next"
              onChangeText={(text) =>
                this.setState({
                  email: text,
                })
              }
              keyboardType={'email-address'}
              value={this.state.email}
            />

            <PasswordInput
              needValidation
              accessibilityLabel="Password"
              label={Localized.Labels.password}
              forwardRef={(r) => (this.password = r)}
              value={this.state.password}
              onFocus={() => this.setState({isKeyboardOpen: true})}
              onSubmitEditing={() => this.handleClick()}
              onChangeText={(password, errorMessage) =>
                this.setState({
                  password,
                  errorMessage,
                })
              }
            />
          </View>
          <AVTouchableOpacity style={styles.boxWeb} onPress={this.handleClick}>
            <View style={styles.letter}>
              <EmailLink size={ScreenWidth * 0.2} />
            </View>
            <AVText style={styles.descText}>
              {Localized.Labels.send_link_to_email}
            </AVText>
            <AVText style={styles.clickText}>
              {Localized.Labels.click_here_to_continue}
            </AVText>
          </AVTouchableOpacity>
        </View>
      </BackSubheader>
    );
  }
}

const styles = StyleSheet.create({
  instructions: {
    color: Styles.black,
    fontSize: Styles.Fonts.f2,
    marginVertical: Styles.Spacing.m1,
    textAlign: 'left',
  },
  bottomRow: {
    marginBottom: Styles.Spacing.m5,
  },
  title: {
    fontSize: Styles.Fonts.f2,
    marginTop: Styles.Spacing.m2,
  },
  boxWeb: {
    borderRadius: 8,
    backgroundColor: '#6d6d76',
    padding: Styles.Spacing.m4,
    marginBottom: Styles.Spacing.m3,
  },
  box: {
    marginTop: Styles.Spacing.m3,
    borderRadius: 8,
    backgroundColor: '#6d6d76',
    paddingHorizontal: Styles.Spacing.m4,
  },
  letter: {
    marginLeft: Styles.Spacing.m2,
    marginTop: Styles.Spacing.m3,
  },
  descText: {
    textAlign: 'right',
    color: Styles.white,
    fontSize: Styles.Fonts.f1,
  },
  clickText: {
    textAlign: 'right',
    fontSize: Styles.Fonts.f3,
    color: Styles.white,
    fontWeight: 'bold',
  },
  container: {
    flex: 1,
    padding: Styles.Spacing.m3,
  },
  passwordRuleText: {
    paddingLeft: 20,
    paddingVertical: 5,
    color: Styles.gray20,
  },
  passwordRule: {
    paddingLeft: 40,
    paddingBottom: 5,
    color: Styles.gray40,
  },
});
export default withForwardedNavigationParams()(
  withScrollToElement(CreateAccountEnterEmail),
);
